import React, { useState, useEffect } from 'react';
import PresetDndContext from '../common/DnD/PresetDndContext';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import FieldListItem from './FieldListItem';
import NewField from './NewField';
import SortableItem from '../common/DnD/SortableItem';
import { handleSortUpdate } from '../common/DnD/sortUpdater';
import {
  handleDestroyField,
  handleCopyField,
  checkFieldResponsesExist,
  hasValidResponses,
} from './constants/handleField';
import {
  adjustSortablesAfterDelete,
  dragSort,
  sortable,
} from '../../helpers/sorters';

const FieldIndex = (props) => {
  const { informantID, setInformantID, fieldset } = props;
  const [fields, setFields] = useState(sortable(props.fields));
  const [fieldValidity, setFieldValidity] = useState([]);
  const [fieldPartitions, setFieldPartitions] = useState([]);

  useEffect(() => {
    checkFieldResponsesExist(fieldset.id).then((res) => {
      if (res.ok) setFieldValidity(res.data.field_validity);
    });
  }, []);

  useEffect(() => {
    determineFieldPartitions();
  }, [fields]);

  // This allows builders to get a sense of form layout with field widths
  const determineFieldPartitions = () => {
    let partitions = [[]];
    fields.forEach((f) => {
      let prevPartition = partitions.at(-1) || [];
      if (
        prevPartition.length >= f.width ||
        (prevPartition.at(-1) && prevPartition.at(-1).width != f.width)
      ) {
        partitions.push([{ id: f.id, width: f.width }]);
      } else {
        prevPartition.push({ id: f.id, width: f.width });
      }
    });
    setFieldPartitions(partitions.flatMap((p) => p.at(-1)?.id));
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      let sort = fields.map((obj) => obj.id).indexOf(over.id);
      let backup = [...fields];
      setFields((fields) => dragSort(fields, active, over));
      let sorted = await handleSortUpdate('fields', active.id, sort);
      if (!sorted.ok) setFields(backup);
    }
  };

  const handleDestroy = async (e, id) => {
    e.preventDefault();
    await handleDestroyField(id, (response) => {
      if (response.ok) setFields(adjustSortablesAfterDelete(fields, id));
    });
  };

  const handleCopy = async (e, id) => {
    e.preventDefault();
    await handleCopyField(id, async (response) => {
      if (response.ok) setFields([...fields, response.data.field]);
    });
  };

  return (
    <section className='fields'>
      <h2>Fields</h2>

      <NewField
        fsID={fieldset.id}
        setFields={setFields}
        sortValue={fields.length}
        informantID={informantID}
        setInformantID={setInformantID}
        setFieldValidity={setFieldValidity}
      />

      <div className='editor'>
        <div className='drop-zone'>
          <PresetDndContext handleDragEnd={handleDragEnd}>
            <ol className='orderable ui-sortable'>
              {fields.length ? (
                <SortableContext
                  strategy={verticalListSortingStrategy}
                  items={fields}
                >
                  {fields.map((field, i) => {
                    return (
                      <React.Fragment key={`list-item-${field.id}`}>
                        <SortableItem id={field.id}>
                          <FieldListItem
                            field={field}
                            key={field.id}
                            handleCopy={handleCopy}
                            handleDestroy={handleDestroy}
                            hasValidResponses={hasValidResponses(
                              fieldValidity,
                              field.id
                            )}
                          />
                        </SortableItem>
                        {fieldPartitions.includes(field.id) &&
                          fields.length - 1 != i && (
                            <hr style={{ margin: '0.4em 0' }} />
                          )}
                      </React.Fragment>
                    );
                  })}
                </SortableContext>
              ) : (
                <li className='empty'>
                  Add some fields to make this fieldset usable...
                </li>
              )}
            </ol>
          </PresetDndContext>
        </div>
      </div>
    </section>
  );
};

export default FieldIndex;
